import "@babylonjs/loaders/glTF";
import { create } from "@bufbuild/protobuf";
import { createScene, startSpectator } from "./engine/scene";
import { buildWorld } from "./engine/worldBuild";
import { PlotBounds } from "./engine/plotBounds";
import { buildRoads } from "./engine/roads";
import { EstateBeam } from "./engine/estateBeam";
import { PlaceMode } from "./engine/placing";
import { MarkerPins } from "./engine/markerPins";
import { MarkerMode } from "./engine/markerMode";
import { mountHUD } from "./hud/hud";
import { mountChat } from "./hud/chat";
import { mountFade } from "./hud/fade";
import { ChatSendSchema } from "./gen/livesoup/v1/realtime_pb";
import { bindKeys } from "./keys";
import { ApiError, getMe, getPlots, getRoads, getWorldConfig } from "./net/api";
import { refreshMyAssets } from "./state/myAssets";
import { client } from "./net/codec";
import { RoomController } from "./room/controller";
import { startWorldSession } from "./session";
import { onChatError, refuseNearInRoom } from "./state/chat";
import { estateAnnouncement, safeStorage } from "./state/estateBanner";
import { $markerPanel, $markers } from "./state/markers";
import { $leaveRequest, $panel, $playerPos, $plots, $session, $worldConfig, toast } from "./state/stores";
import { describeError } from "./ui/errors";
import { mountUI } from "./ui/mount";
import type { WorldConfig } from "./world/config";
import { bindTouchMode } from "./state/touch";
import { input } from "./input/state";
import { mountTouchLayer } from "./hud/touch/layer";
import { mountPortrait } from "./hud/touch/portrait";
import { mountTouchButtons } from "./hud/touch/buttons";
import { releaseAllPressed } from "./hud/touch/press";
import { bindGyro, bindGyroPermission, motionPermission } from "./input/gyro";
import { GYRO_DENIED, gyroActive, initGyro } from "./state/gyro";
import { bindViewport } from "./hud/touch/viewport";
import { bindFirstTouchFullscreen } from "./hud/touch/fullscreen";
import { mountFps } from "./hud/touch/fps";

async function boot(): Promise<void> {
  bindTouchMode();
  bindViewport();
  bindFirstTouchFullscreen();
  const canvas = document.getElementById("render") as HTMLCanvasElement;
  const { engine, scene, atmosphere, view } = createScene(canvas);
  mountFps(document.getElementById("hud")!, engine);
  mountUI(document.getElementById("ui")!);

  const { config } = await getWorldConfig();
  const cfg = config as WorldConfig;
  $worldConfig.set(cfg);
  const world = buildWorld(scene, cfg, atmosphere);
  world.terrain.update(0, 0);
  // Roads are decoration: a failed read leaves the town without them and nothing else changes (spec 2e §3.3).
  getRoads().then((r) => buildRoads(scene, r, world.height)).catch((err) => console.error("roads", err));
  const stopSpectator = startSpectator(scene);

  try {
    const me = await getMe();
    $session.set(me);
    $panel.set(null);
    stopSpectator();
    const hud = mountHUD(document.getElementById("hud")!);
    const session = startWorldSession(scene, canvas, world, hud);
    const room = new RoomController({ engine, lobby: scene, show: (s) => view.show(s), canvas, session, hud, fade: mountFade(document.getElementById("hud")!) });
    room.startInRoom(); // a fresh page opens in your own room (spec 2o §2.1)
    const placing = new PlaceMode(scene, session, world, session.entities, hud);
    const bounds = new PlotBounds(scene, world.height);
    const beam = new EstateBeam(scene, world.height);
    $plots.subscribe((p) => {
      bounds.sync(p);
      const me = $session.get()?.id ?? null;
      beam.setEstate(p.find((x) => x.kind === "estate" && x.owner_id === me) ?? null);
      const msg = estateAnnouncement(p, me, safeStorage());
      if (msg) toast(msg, 6000);
    });
    $playerPos.subscribe((pos) => beam.update(pos.x, pos.z));
    const chat = mountChat(document.getElementById("hud")!, {
      send: (channelId, body) => {
        if (refuseNearInRoom(channelId)) { // after noteSent, so the text comes back as the draft
          queueMicrotask(() => { onChatError("CHAT_IN_ROOM"); toast(describeError("CHAT_IN_ROOM")); });
          return;
        }
        session.ws.send(client({ case: "chatSend", value: create(ChatSendSchema, { channelId, body }) }));
      },
      leave: () => $leaveRequest.set($leaveRequest.get() + 1),
      setBlocked: (b) => room.activePlayer().setMovementBlocked(b || !!$panel.get()),
    });
    const marker = new MarkerMode(scene, session, world, session.entities, hud);
    const cmd = bindKeys({ session, placing, marker, entities: session.entities, hud, canvas, chat, room });
    const touchLayer = mountTouchLayer(document.getElementById("hud")!, input);
    mountPortrait(() => { touchLayer.reset(); releaseAllPressed(); });
    mountTouchButtons(document.getElementById("hud")!, cmd, input);
    initGyro();
    bindGyro(input, window, { on: () => gyroActive() });
    bindGyroPermission(window, motionPermission(), () => gyroActive(), () => toast(GYRO_DENIED, 6000));
    const pins = new MarkerPins(scene, world.height);
    const syncPins = () => pins.sync($plots.get(), $markers.get());
    $markers.subscribe(syncPins);
    $plots.subscribe(syncPins);
    $markerPanel.subscribe((p) => pins.showPreview(p?.mode === "create" ? p.at : null));
    $plots.set(await getPlots());
    await refreshMyAssets();
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      $panel.set("login");
      return;
    }
    throw err;
  }
}

boot().catch((err) => {
  console.error(err);
  document.getElementById("ui")!.textContent = "啟動失敗：" + String(err);
});
